import React, { Component, ReactNode } from 'react';

const addLogs = async (message: string) => {
  try {
    const url = 'Utils/Log';

    await fetch(url, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  } catch (err) {
    return undefined;
  }
};

class ErrorBoundary extends Component<{ children: ReactNode; name?: string; props?: object }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  trimProps(props: object, maxLength: number = 100) {
    const trimmedProps: { [key: string]: string } = {};

    for (const key in props) {
      if (Object.prototype.hasOwnProperty.call(props, key)) {
        let value = (props as any)[key];

        if (typeof value === 'object' && value !== null) {
          value = JSON.stringify(value);
        } else if (typeof value !== 'string') {
          value = String(value);
        }

        if (value.length > maxLength) {
          value = value.substring(0, maxLength) + '...';
        }

        trimmedProps[key] = value;
      }
    }

    return trimmedProps;
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { name, props } = this.props;

    let trimmedProps;

    try {
      trimmedProps = this.trimProps(props || {});
    } catch (err) {
      trimmedProps = { err: 'logger' };
    }

    const browser = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenSize: `${window.screen.width}x${window.screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    };

    const logMessage = `[${new Date().toISOString()}]\n
        Component: ${name || 'Unknown'}\n
        Props: ${JSON.stringify(trimmedProps)}\n
        Error: ${error.message}\n
        Stack: ${error.stack}\n
        Component Stack: ${errorInfo.componentStack}\n
        Url: ${window.location.href}\n
        Browser: ${JSON.stringify(browser)}\n`;

    addLogs(logMessage);
  }

  render() {
    if (this.state.hasError) {
      return (
        <h1 style={{ width: '100%', textAlign: 'center', paddingTop: 20, fontWeight: 'bold' }}>
          OOPS
        </h1>
      );
    }
    return this.props.children;
  }
}

const withErrorBoundary = <P extends object>(Component: React.ComponentType<P>, name?: string) => {
  const componentName = name || Component.displayName || Component.name || 'Unknown';

  return (props: P) => (
    <ErrorBoundary name={componentName} props={props}>
      <Component {...props} />
    </ErrorBoundary>
  );
};

export default withErrorBoundary;
